import { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import CloseButton from './CloseButton';

type AlertVariant = 'success' | 'error' | 'warning' | 'info';

interface AlertProps {
  variant: AlertVariant;
  title?: string;
  /**
   * ReactNode rather than string. An error surfaced from an API is often
   * already a node — a message with a link in it, or a list of failed rows —
   * and the string-only signature is part of why ~479 hand-rolled alert boxes
   * grew up beside this component instead of using it.
   */
  message: ReactNode;
  onClose?: () => void;
  actions?: ReactNode;
  className?: string;
}

export default function Alert({
  variant,
  title,
  message,
  onClose,
  actions,
  className = '',
}: AlertProps) {
  const variantStyles = {
    success: {
      container: 'bg-green-50 border-green-200',
      icon: 'text-green-600',
      title: 'text-green-800',
      message: 'text-green-700',
      IconComponent: CheckCircleIcon,
    },
    error: {
      container: 'bg-red-50 border-red-200',
      icon: 'text-red-600',
      title: 'text-red-800',
      message: 'text-red-700',
      IconComponent: XCircleIcon,
    },
    warning: {
      container: 'bg-amber-50 border-amber-200',
      icon: 'text-amber-600',
      title: 'text-amber-800',
      message: 'text-amber-700',
      IconComponent: ExclamationTriangleIcon,
    },
    info: {
      container: 'bg-blue-50 border-blue-200',
      icon: 'text-blue-600',
      title: 'text-blue-800',
      message: 'text-blue-700',
      IconComponent: InformationCircleIcon,
    },
  };

  const styles = variantStyles[variant];
  const Icon = styles.IconComponent;

  return (
    <div className={`rounded-lg border p-4 ${styles.container} ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Icon className={`h-5 w-5 ${styles.icon}`} aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          {title && <h3 className={`text-sm font-semibold ${styles.title}`}>{title}</h3>}
          <div className={`text-sm ${styles.message} ${title ? 'mt-1' : ''}`}>{message}</div>
          {actions && <div className="mt-3 flex gap-2">{actions}</div>}
        </div>
        {onClose && (
          <div className="ml-auto pl-3">
            <CloseButton onClick={onClose} size="sm" label="Dismiss alert" />
          </div>
        )}
      </div>
    </div>
  );
}
